/*
 * /markdown-preview — рабочее место: открыть, прочитать, поправить, отдать.
 *
 * Что было и почему переделано (замер 07.09.2026):
 *   • Тул позиционировался как «редактор с превью», а спрос в Wordstat стоит
 *     в другом месте: «открыть файл md» 2 468/мес, «чем открыть md файл»
 *     1 220, «md файл онлайн» 350, «просмотр md файлов» 214 — против
 *     «markdown редактор» 612. Людей с ГОТОВЫМ файлом кратно больше, а
 *     открыть его можно было только перетаскиванием в текстовое поле.
 *   • Второй по величине спрос — выход: pdf 403, word 308, docx 93, html 115.
 *     Выходов было три, из них .html без единого стиля, а PDF — растровым
 *     снимком экрана.
 *   • Управление стояло тремя подписанными группами и шестью кнопками в один
 *     ряд, панель форматирования переносилась в две строки, и на телефоне
 *     редактор начинался ниже сгиба.
 *
 * Раскладка живёт на общем слое partials/tool-console (.tc): он даёт прибору
 * 1240 px вместо 900, уплотняет шапку страницы и — главное — рефлоу по
 * КОНТЕЙНЕРУ, а не по вьюпорту.
 */
import { ACTIONS, handleEnter, handleTab, formatTableAtCursor, formatAllTables, replaceRange } from './editor.js';
import { parse, stampLines, highlightIn, renderMermaid, renderMath, buildOutline, wireTaskLists, stats } from './render.js';
import { htmlToMarkdown, isRichHtml } from './html2md.js';
import * as files from './files.js';
import * as out from './exporters.js';

const CFG = window.__mdCfg || {};
const T = CFG.t || {};
const $ = (id) => document.getElementById(id);
const toast = (m, k) => (window.__toast || (() => {}))(m, k || 'success');
const ae = (name, data) => { try { window.__aeFunnel ? window.__aeFunnel(name, data || {}) : (window.__ae && window.__ae(name, data || {})); } catch (e) {} };

const ta = $('mdEditor');
const pv = $('mdPreview');
const app = $('mdApp');
const bar = $('mdBar');

const STORE = 'md-preview-draft-v2';
const state = {
    name: '',            // имя открытого файла
    handle: null,        // FileSystemFileHandle, если браузер умеет
    dirty: false,
    view: 'split',
};

/* ── рендер ─────────────────────────────────────────────────────────────── */
let renderTimer = null, lastLines = [];

function render() {
    const src = ta.value;
    let res;
    try {
        res = parse(src, { gfm: $('mdGfm').checked, breaks: $('mdBreaks') && $('mdBreaks').checked });
    } catch (e) {
        pv.textContent = T.err_load || 'Markdown engine failed to load';
        return;
    }
    pv.innerHTML = res.html;
    lastLines = res.lines;
    stampLines(pv, res.lines);
    highlightIn(pv, CFG);
    renderMermaid(pv, CFG);
    renderMath(pv, CFG);
    wireTaskLists(pv, () => ta.value, (next) => {
        const p = ta.selectionStart;
        replaceRange(ta, 0, ta.value.length, next);
        ta.setSelectionRange(p, p);
        scheduleRender();
        markDirty();
    });
    paintOutline(buildOutline(pv));
    paintStats(stats(src, pv));
    setEmpty(!src.trim());
}

function scheduleRender() {
    if (renderTimer) clearTimeout(renderTimer);
    renderTimer = setTimeout(() => { renderTimer = null; render(); }, 140);
}

function paintStats(s) {
    const box = $('mdStats');
    if (!box) return;
    box.innerHTML = '';
    const add = (v, l) => {
        const b = document.createElement('span');
        b.className = 'md-stat';
        b.innerHTML = '<b></b><i></i>';
        b.querySelector('b').textContent = v;
        b.querySelector('i').textContent = l;
        box.appendChild(b);
    };
    add(s.words, T.st_words);
    add(s.chars, T.st_chars);
    if (s.headings) add(s.headings, T.st_headings);
    add(s.minutes, T.st_read);
}

function setEmpty(isEmpty) {
    app.classList.toggle('is-empty', isEmpty);
}

/* ── оглавление ─────────────────────────────────────────────────────────── */
function paintOutline(items) {
    const box = $('mdOutline');
    if (!box) return;
    box.textContent = '';
    if (!items.length) {
        const p = document.createElement('p');
        p.className = 'md-out-empty';
        p.textContent = T.outline_empty;
        box.appendChild(p);
        return;
    }
    const min = Math.min(...items.map(i => i.level));
    for (const it of items) {
        const a = document.createElement('button');
        a.type = 'button';
        a.className = 'md-out-item';
        a.style.paddingInlineStart = (8 + (it.level - min) * 12) + 'px';
        a.dataset.level = String(it.level);
        a.textContent = it.text;
        a.addEventListener('click', () => {
            const el = pv.querySelector('#' + CSS.escape(it.id));
            if (el) el.scrollIntoView({ block: 'nearest' });
            if (it.line) jumpEditorToLine(it.line);
            ae('outline_jump', { level: it.level });
        });
        box.appendChild(a);
    }
}

function jumpEditorToLine(line) {
    const idx = ta.value.split('\n').slice(0, Math.max(0, line - 1)).join('\n').length;
    ta.focus();
    ta.setSelectionRange(idx, idx);
    const lh = parseFloat(getComputedStyle(ta).lineHeight) || 22;
    ta.scrollTop = Math.max(0, (line - 2) * lh);
}

/* ── синхронная прокрутка по НОМЕРУ СТРОКИ ──────────────────────────────
   Раньше считались проценты от высоты, и на документе с таблицей или кодом
   панели показывали разные места. Здесь: строка в редакторе → ближайший блок
   превью с таким data-line, и наоборот. */
let lock = 0;
function lineHeightOf(el) { return parseFloat(getComputedStyle(el).lineHeight) || 22; }

function topLineInEditor() {
    return Math.floor(ta.scrollTop / lineHeightOf(ta)) + 1;
}

function blockForLine(line) {
    const kids = pv.children;
    let best = null;
    for (let i = 0; i < kids.length; i++) {
        const l = Number(kids[i].dataset.line || 0);
        if (!l) continue;
        if (l <= line) best = kids[i]; else break;
    }
    return best;
}

function syncToPreview() {
    if (lock || !$('mdSync').checked || state.view !== 'split') return;
    lock = 1;
    const line = topLineInEditor();
    const el = blockForLine(line);
    if (el) {
        const prev = el.previousElementSibling;
        const startLine = Number(el.dataset.line || line);
        const nextEl = el.nextElementSibling;
        const endLine = nextEl ? Number(nextEl.dataset.line || startLine + 1) : startLine + 1;
        const frac = endLine > startLine ? Math.min(1, Math.max(0, (line - startLine) / (endLine - startLine))) : 0;
        pv.scrollTop = el.offsetTop - pv.offsetTop + frac * el.offsetHeight;
    } else {
        pv.scrollTop = 0;
    }
    requestAnimationFrame(() => { lock = 0; });
}

function syncToEditor() {
    if (lock || !$('mdSync').checked || state.view !== 'split') return;
    lock = 1;
    const kids = pv.children;
    let line = 1;
    for (let i = 0; i < kids.length; i++) {
        if (kids[i].offsetTop - pv.offsetTop <= pv.scrollTop + 4) line = Number(kids[i].dataset.line || line);
        else break;
    }
    ta.scrollTop = Math.max(0, (line - 1) * lineHeightOf(ta));
    requestAnimationFrame(() => { lock = 0; });
}

/* ── вид ────────────────────────────────────────────────────────────────
   Узко — «бок о бок» невозможно физически, поэтому переключатель сам
   перестраивается по ШИРИНЕ КОНТЕЙНЕРА: два варианта вместо трёх. Это и есть
   пропорциональный перенос, а не медиазапрос по окну. */
function setView(v, remember) {
    state.view = v;
    app.dataset.view = v;
    document.querySelectorAll('[data-view-btn]').forEach((b) => {
        const on = b.dataset.viewBtn === v;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (remember && window.__toolSettings) window.__toolSettings.save('markdown-preview', 'view', v);
    if (v !== 'editor') requestAnimationFrame(render);
}

let narrow = false;
function applyWidth() {
    const w = app.clientWidth;
    // clientWidth 0 — это не «узкий экран», а невидимая вкладка/переход
    if (!w) return;
    const isNarrow = w < 700;
    if (isNarrow === narrow) return;
    narrow = isNarrow;
    app.classList.toggle('is-narrow', isNarrow);
    if (isNarrow && state.view === 'split') setView('editor', false);
    if (!isNarrow && !state.userChoseView) setView('split', false);
}

/* ── файл ───────────────────────────────────────────────────────────────── */
function markDirty() {
    if (state.dirty) return;
    state.dirty = true;
    paintDoc();
}

function paintDoc() {
    const nameEl = $('mdDocName');
    if (!nameEl) return;
    nameEl.textContent = state.name || T.doc_untitled;
    nameEl.title = state.name || T.doc_untitled;
    app.classList.toggle('has-file', !!state.name);
    app.classList.toggle('is-dirty', state.dirty);
    const save = $('mdSave');
    if (save) save.hidden = !state.handle;
}

function adoptDocument(name, text, handle) {
    replaceRange(ta, 0, ta.value.length, text);
    ta.setSelectionRange(0, 0);
    ta.scrollTop = 0;
    state.name = name || '';
    state.handle = handle || null;
    state.dirty = false;
    paintDoc();
    render();
    persist();
    ae('file_opened', { chars: text.length, via: handle ? 'picker' : 'input' });
}

async function openViaPicker() {
    try {
        const r = await files.openWithPicker();
        if (r) adoptDocument(r.name, r.text, r.handle);
    } catch (e) {
        if (e && e.name === 'AbortError') return;      // человек закрыл диалог
        fallbackOpen();
    }
}

function fallbackOpen() {
    const input = $('mdFile');
    if (input) input.click();
}

function openFile() {
    if (files.canPickFiles()) return openViaPicker();
    fallbackOpen();
}

async function takeFile(file) {
    if (!files.looksLikeText(file)) { toast(T.err_not_text, 'warning'); return; }
    try {
        const r = await files.readFile(file);
        adoptDocument(r.name, r.text, null);
    } catch (e) {
        toast(e.message === 'too_big' ? T.err_too_big : T.err_read, 'warning');
    }
}

async function saveDocument() {
    if (state.handle) {
        try {
            const ok = await files.saveToHandle(state.handle, ta.value);
            if (ok) { state.dirty = false; paintDoc(); toast(T.saved); ae('file_saved', { via: 'handle' }); return; }
        } catch (e) { /* разрешение отозвано — уходим в «сохранить как» */ }
    }
    if (typeof window.showSaveFilePicker === 'function') {
        try {
            const r = await files.saveAs(ta.value, (files.baseName(state.name)) + '.md');
            if (r) { state.name = r.name; state.handle = r.handle; state.dirty = false; paintDoc(); toast(T.saved); ae('file_saved', { via: 'saveas' }); }
            return;
        } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    out.saveMarkdown(ta.value, files.baseName(state.name));
    ae('file_saved', { via: 'download' });
}

/* ── черновик ───────────────────────────────────────────────────────────── */
let saveTimer = null;
function persist() {
    try { localStorage.setItem(STORE, JSON.stringify({ text: ta.value, name: state.name, at: Date.now() })); } catch (e) {}
}
function schedulePersist() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(persist, 500);
}
function restore() {
    try {
        const raw = localStorage.getItem(STORE);
        if (!raw) return false;
        const d = JSON.parse(raw);
        if (!d || typeof d.text !== 'string' || !d.text.trim()) return false;
        ta.value = d.text;
        state.name = d.name || '';
        return true;
    } catch (e) { return false; }
}

/* ── вставка с оформлением → Markdown ───────────────────────────────────── */
ta.addEventListener('paste', (e) => {
    const dt = e.clipboardData;
    if (!dt) return;
    const html = dt.getData('text/html');
    if (!html || !isRichHtml(html)) return;              // обычный текст — обычная вставка
    e.preventDefault();
    const md = htmlToMarkdown(html).trim();
    replaceRange(ta, ta.selectionStart, ta.selectionEnd, md);
    scheduleRender();
    markDirty();
    schedulePersist();
    toast(T.pasted_converted);
    ae('paste_html_converted', { chars: md.length });
});

/* ── набор ──────────────────────────────────────────────────────────────── */
ta.addEventListener('input', () => { scheduleRender(); markDirty(); schedulePersist(); });
ta.addEventListener('scroll', syncToPreview, { passive: true });
pv.addEventListener('scroll', syncToEditor, { passive: true });

ta.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { if (handleEnter(ta, e)) { scheduleRender(); markDirty(); } return; }
    if (e.key === 'Tab') { if (handleTab(ta, e)) { scheduleRender(); markDirty(); } return; }
    if (!(e.ctrlKey || e.metaKey)) return;
    const k = e.key.toLowerCase();
    if (k === 's') { e.preventDefault(); saveDocument(); return; }
    if (e.shiftKey && k === 'f') { e.preventDefault(); if (formatTableAtCursor(ta)) { scheduleRender(); markDirty(); toast(T.table_formatted); } return; }
    const map = { b: 'bold', i: 'italic', k: 'link', e: 'code', '1': 'h1', '2': 'h2', '3': 'h3' };
    if (map[k]) { e.preventDefault(); ACTIONS[map[k]](ta, T); scheduleRender(); markDirty(); }
});

/* ── панель форматирования ──────────────────────────────────────────────── */
document.querySelectorAll('[data-md]').forEach((b) => b.addEventListener('click', () => {
    const fn = ACTIONS[b.dataset.md];
    if (!fn) return;
    fn(ta, T);
    scheduleRender();
    markDirty();
    schedulePersist();
    ae('format_click', { a: b.dataset.md });
}));

/* ── перетаскивание файла на всю рабочую область ────────────────────────── */
let dragDepth = 0;
['dragenter', 'dragover'].forEach(ev => app.addEventListener(ev, (e) => {
    if (!e.dataTransfer || ![...e.dataTransfer.types].includes('Files')) return;
    e.preventDefault();
    if (ev === 'dragenter') dragDepth++;
    app.classList.add('is-drop');
}));
app.addEventListener('dragleave', () => { if (--dragDepth <= 0) { dragDepth = 0; app.classList.remove('is-drop'); } });
app.addEventListener('drop', (e) => {
    if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
    e.preventDefault();
    dragDepth = 0;
    app.classList.remove('is-drop');
    takeFile(e.dataTransfer.files[0]);
});

/* вторая дверь — зона в пустом состоянии */
const fileInput = $('mdFile');
if (fileInput) fileInput.addEventListener('change', () => {
    const f = fileInput.files && fileInput.files[0];
    // input.value чистим, чтобы повторный выбор ТОГО ЖЕ файла снова дал change
    fileInput.value = '';
    if (f) takeFile(f);
});
const pick = $('mdPick');
if (pick) pick.addEventListener('click', openFile);

/* ── шапка документа ────────────────────────────────────────────────────── */
$('mdOpen').addEventListener('click', openFile);
const saveBtn = $('mdSave');
if (saveBtn) saveBtn.addEventListener('click', saveDocument);

/* ── вид, оглавление, опции ─────────────────────────────────────────────── */
document.querySelectorAll('[data-view-btn]').forEach((b) => b.addEventListener('click', () => {
    state.userChoseView = true;
    setView(b.dataset.viewBtn, true);
    ae('view_change', { v: b.dataset.viewBtn });
}));

// один переключатель в двух местах: значок в панели на широком экране и
// пункт меню «Ещё» на узком — состояние общее, поэтому и aria тоже
const outToggles = [...document.querySelectorAll('[data-outline-toggle]')];
function paintOutlineToggle(on) {
    outToggles.forEach(b => b.setAttribute('aria-pressed', on ? 'true' : 'false'));
}
outToggles.forEach(b => b.addEventListener('click', () => {
    const on = app.classList.toggle('has-outline');
    paintOutlineToggle(on);
    if (window.__toolSettings) window.__toolSettings.save('markdown-preview', 'outline', on ? 1 : 0);
    ae('outline_toggle', { on: on ? 1 : 0 });
    closeMenus();
}));

$('mdGfm').addEventListener('change', render);
if ($('mdBreaks')) $('mdBreaks').addEventListener('change', render);

const themeSel = $('mdTheme');
function applyTheme(v) {
    if (v === 'auto') pv.removeAttribute('data-md-theme');
    else pv.setAttribute('data-md-theme', v);
}
themeSel.addEventListener('change', () => {
    applyTheme(themeSel.value);
    if (window.__toolSettings) window.__toolSettings.save('markdown-preview', 'theme', themeSel.value);
    ae('theme_change', { t: themeSel.value });
});

/* ── выдача ─────────────────────────────────────────────────────────────── */
function themeCss() {
    // Стили превью, вшиваемые в самодостаточный .html
    const sheet = document.getElementById('mdExportCss');
    return sheet ? sheet.textContent : '';
}

const EXPORTS = {
    md: () => { out.saveMarkdown(ta.value, files.baseName(state.name)); },
    html: () => { out.saveHtml(pv, files.baseName(state.name), state.name || T.doc_untitled, themeCss()); },
    docx: async () => {
        toast(T.preparing, 'info');
        try { await out.saveDocx(pv, files.baseName(state.name), CFG); }
        catch (e) { toast(T.err_export, 'warning'); }
    },
    pdf: () => out.printDocument(),
    copy: async () => {
        const kind = await out.copyRich(pv, ta.value);
        toast(kind === 'rich' ? T.copied_rich : T.copied_plain);
    },
    copyhtml: async () => { await out.copyHtmlSource(pv); toast(T.copied_html); },
};

document.querySelectorAll('[data-export]').forEach((b) => b.addEventListener('click', () => {
    const fn = EXPORTS[b.dataset.export];
    if (!fn) return;
    fn();
    ae('export', { f: b.dataset.export });
    closeMenus();
}));

/* ── меню ───────────────────────────────────────────────────────────────── */
function closeMenus() {
    document.querySelectorAll('.md-menu[data-open]').forEach((m) => {
        m.removeAttribute('data-open');
        const btn = m.previousElementSibling;
        if (btn) btn.setAttribute('aria-expanded', 'false');
    });
}
document.querySelectorAll('[data-menu]').forEach((btn) => {
    const menu = document.getElementById(btn.dataset.menu);
    if (!menu) return;
    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = menu.hasAttribute('data-open');
        closeMenus();
        if (!open) { menu.setAttribute('data-open', ''); btn.setAttribute('aria-expanded', 'true'); }
    });
});
document.addEventListener('click', closeMenus);
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenus(); });

/* ── шаблоны ────────────────────────────────────────────────────────────
   «readme md» — 1 691 показ/мес, «создать readme md» 43: чистый лист это не
   то, с чего человек хочет начать. Шаблон отвечает на вопрос «а как вообще
   выглядит хороший README». */
document.querySelectorAll('[data-template]').forEach((b) => b.addEventListener('click', () => {
    const tpl = (CFG.templates || {})[b.dataset.template];
    if (!tpl) return;
    if (ta.value.trim() && !confirm(T.confirm_replace)) return;
    adoptDocument(b.dataset.template + '.md', tpl, null);
    ae('template', { t: b.dataset.template });
    closeMenus();
}));

/* ── прочие действия ────────────────────────────────────────────────────── */
const fmtAll = $('mdFormatTables');
if (fmtAll) fmtAll.addEventListener('click', () => {
    const r = formatAllTables(ta.value);
    if (!r.count) { toast(T.no_tables, 'info'); return; }
    const p = ta.selectionStart;
    replaceRange(ta, 0, ta.value.length, r.text);
    ta.setSelectionRange(Math.min(p, r.text.length), Math.min(p, r.text.length));
    scheduleRender();
    markDirty();
    toast(T.tables_formatted.replace(':n', r.count));
    ae('tables_formatted', { n: r.count });
    closeMenus();
});

const clearBtn = $('mdClear');
if (clearBtn) clearBtn.addEventListener('click', () => {
    if (ta.value.trim() && !confirm(T.confirm_clear)) return;
    adoptDocument('', '', null);
    closeMenus();
});

/* ── уход со страницы с несохранённым файлом ────────────────────────────── */
window.addEventListener('beforeunload', (e) => {
    persist();
    if (state.handle && state.dirty) { e.preventDefault(); e.returnValue = ''; }
});

/* ── старт ──────────────────────────────────────────────────────────────── */
function boot() {
    if (window.__toolSettings) {
        const th = window.__toolSettings.load('markdown-preview', 'theme', 'auto');
        themeSel.value = th; applyTheme(th);
        if (Number(window.__toolSettings.load('markdown-preview', 'outline', 0))) {
            app.classList.add('has-outline');
            paintOutlineToggle(true);
        }
        const v = window.__toolSettings.load('markdown-preview', 'view', '');
        if (v) { state.userChoseView = true; state.view = v; }
    }
    if (!restore() && CFG.starter) ta.value = CFG.starter;
    paintDoc();
    setView(state.view, false);
    applyWidth();
    render();
    if (window.ResizeObserver) new ResizeObserver(applyWidth).observe(app);
    else window.addEventListener('resize', applyWidth);
}

let waited = 0;
(function waitForMarked() {
    if (window.marked && window.DOMPurify) { boot(); return; }
    waited += 60;
    if (waited > 12000) { pv.textContent = T.err_load; setEmpty(false); return; }
    setTimeout(waitForMarked, 60);
})();
